import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { cloneDeep } from 'lodash'

import {
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Fab,
  Tooltip,
  Select,
  MenuItem,
} from '@mui/material'

import { Add, Info } from '@mui/icons-material'
import FormLabel from '@mui/material/FormLabel'
import { addKeyToObjectsArray } from 'utils/addUniqueKeyToObjects'
import { kinshipRelationshipTypes } from 'utils/constants/DueDiligenceRelations'
import { removeAttachment } from 'utils/attachmentOperations'
import { formatToCPF, isCPF } from 'brazilian-values'
import AddAttachments from 'components/AddAttachments'
import { useSelector } from 'react-redux'
import { hasAnswerChanged } from 'utils/functions'
import { OPERATION_TYPES } from 'utils/constants/operationTypes'

import styles from './styles'
import AnswerCard from '../AnswerCard'
import QuestionButton from '../QuestionButton'
import AttachmentsList from '../../../AttachmentsList'

export default function QuestionEleven(props) {
  const { questionData, saveResponses, formOwner } = props
  const classes = styles()
  const token = useSelector((state) => state.auth.access_token)

  const initialData = {
    name: '',
    cpf: '',
    degree_of_kinship: 'default',
    attachments: [],
  }

  const initialErrors = {
    name: false,
    cpf: false,
    degree_of_kinship: false,
    attachments: false,
  }

  const initializeFormState = (data) => {
    return {
      hasProxyActivities: data.items.length > 0,
      items: data.items,
    }
  }

  const [formState, setFormState] = useState(initializeFormState(questionData))

  const [activityData, setActivityData] = useState(initialData)

  const [activityErrors, setActivityErrors] = useState(initialErrors)

  const [attachmentsToRemove, setAttachmentsToRemove] = useState([])

  const [loading, setLoading] = useState(false)

  const [edited, setEdited] = useState(false)
  const [saved, setSaved] = useState(questionData.has_answer)

  const handleFieldChange = (event) => {
    const key = event.target.id
    const newActivityData = cloneDeep(activityData)
    const value =
      key === 'cpf' ? formatToCPF(event.target.value) : event.target.value
    if (key === 'cpf' && value?.length >= 15) {
      return
    }
    newActivityData[key] = value
    setActivityData(newActivityData)
  }

  const handleRadioChange = (event) => {
    const newFormState = cloneDeep(formState)
    newFormState.hasProxyActivities = event.target.value === 'yes'
    setFormState(newFormState)
  }

  const handleSelectChange = (event) => {
    const newActivityData = cloneDeep(activityData)
    newActivityData.degree_of_kinship = event.target.value
    setActivityData(newActivityData)
  }

  const hasProxyActivities = () => {
    return !(formState.items.length === 0)
  }

  const validateRequiredFields = () => {
    const newActivityErrors = initialErrors

    Object.keys(initialErrors).forEach((key) => {
      if (key === 'degree_of_kinship' && activityData[key] === 'default') {
        newActivityErrors[key] = true
      }
      if (key === 'cpf' && !isCPF(activityData[key])) {
        newActivityErrors[key] = true
      }
      if (key === 'attachments' && activityData[key].length === 0) {
        newActivityErrors[key] = true
      }
      if (activityData[key] === '') {
        newActivityErrors[key] = true
      }
    })

    setActivityErrors(newActivityErrors)

    return !Object.keys(newActivityErrors).some((key) => newActivityErrors[key])
  }

  const handleValidateAndUpdateState = () => {
    if (validateRequiredFields()) {
      const newFormState = cloneDeep(formState)
      newFormState.items.push(activityData)
      setFormState(newFormState)
      setActivityData(initialData)
    } else {
      toast.warn(
        'Favor, verifique se todos os campos obrigatórios foram preenchidos corretamente',
      )
    }
  }

  const handleRemoveActivity = (position) => {
    const newFormState = cloneDeep(formState)
    const attachmentsToDelete = newFormState.items[position].attachments
    setAttachmentsToRemove([...attachmentsToRemove, ...attachmentsToDelete])
    newFormState.items = newFormState.items.filter(
      (activity, index) => index !== position,
    )
    setFormState(newFormState)
  }

  const handleRemoveExcludedAttachments = async (attachments) => {
    Promise.all(
      attachments.map((attachment) => removeAttachment(attachment.uuid, token)),
    ).then(() => {
      setAttachmentsToRemove([])
    })
  }

  const validateAndSave = async (skipValidation = false) => {
    if (formState.items.length > 0 && formState.hasProxyActivities === false) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
      return
    }
    const newQuestionData = cloneDeep(questionData)
    if (
      !skipValidation &&
      formState.hasProxyActivities &&
      !hasProxyActivities()
    ) {
      toast.error(
        'Ao selecionar "Sim" deve-se adicionar ao menos uma atividade',
      )
    } else {
      newQuestionData.has_answer = true
      newQuestionData.items = formState.items
      setLoading(true)
      await saveResponses(
        newQuestionData,
        'attorney_activities',
        !saved ? OPERATION_TYPES.CREATE : OPERATION_TYPES.UPDATE,
      )
        .then(async () => {
          await handleRemoveExcludedAttachments(attachmentsToRemove)
          setSaved(true)
          setEdited(false)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const handleUpdateAttachments = (attachments) => {
    const newActivityData = cloneDeep(activityData)
    newActivityData.attachments = attachments
    setActivityData(newActivityData)
  }

  const buildActivity = (activityInfo) => {
    return (
      <React.Fragment>
        <p>
          <span>Nome completo do cliente: </span>
          {activityInfo.name}
        </p>
        <p>
          <span>CPF: </span>
          {activityInfo.cpf}
        </p>

        <p>
          <span>Grau de parentesco: </span>
          {kinshipRelationshipTypes[activityInfo.degree_of_kinship]}
        </p>
        <AttachmentsList attachments={activityInfo.attachments} />
      </React.Fragment>
    )
  }

  useEffect(() => {
    const hasChanged =
      hasAnswerChanged(questionData.items, formState.items) ||
      formState.hasProxyActivities !== questionData.items.length > 0
    setEdited(hasChanged)
    if (formState.items.length > 0 && formState.hasProxyActivities === false) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
    }
  }, [formState])

  useEffect(() => {
    setSaved(questionData.has_answer)
    setFormState(initializeFormState(questionData))
  }, [questionData])

  useEffect(() => {
    if (hasAnswerChanged(questionData.items, formState.items))
      validateAndSave(true)
  }, [formState.items])

  return (
    <div className={classes.root}>
      <div className={classes.formContainer}>
        <h3>Questão 11</h3>
        <div className={classes.form}>
          <FormControl>
            <FormLabel>
              Você atua como procurador de algum cliente na XP ou em outra
              instituição?{' '}
              <Tooltip
                title="Prezado(a), 
                Conforme a Resolução CVM Nº 16, art. 18, III, é vedado que o AAI
                seja procurador ou representante perante instituições 
                integrantes do sistema de distribuição de valores mobiliários,
                excepcionando-se no caso de filhos menores de idade, conforme
                ratificado no Ofício Circular CVM 04/2018, nº 72 e 73.
                Assim, orientamos que encerre a procuração com o(s cliente(s), 
                após volte nesse processo, confirmando a adequação e anexando 
                evidência da regularidade."
              >
                <Info color="info" size="small" />
              </Tooltip>
            </FormLabel>
            <RadioGroup
              row
              value={formState.hasProxyActivities ? 'yes' : 'no'}
              name="hasRelations"
              onChange={handleRadioChange}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Sim" />
              <FormControlLabel value="no" control={<Radio />} label="Não" />
            </RadioGroup>
          </FormControl>

          {formState.hasProxyActivities ? (
            <div className={classes.formFields}>
              <TextField
                id="name"
                label="Nome completo do(a) colaborador(a) XP"
                onChange={handleFieldChange}
                required
                fullWidth
                value={activityData.name}
                error={activityErrors.name}
                size="small"
              />

              <TextField
                id="cpf"
                label="CPF"
                onChange={handleFieldChange}
                required
                value={activityData.cpf}
                error={activityErrors.cpf}
                fullWidth
                size="small"
              />

              <Select
                onChange={handleSelectChange}
                value={activityData.degree_of_kinship}
                error={activityErrors.degree_of_kinship}
                size="small"
              >
                <MenuItem value="default" disabled>
                  Selecione o grau de parentesco
                </MenuItem>
                {Object.keys(kinshipRelationshipTypes).map((key) => (
                  <MenuItem key={key} value={key}>
                    {kinshipRelationshipTypes[key]}
                  </MenuItem>
                ))}
              </Select>

              <AddAttachments
                targetFilename={`${formOwner?.prospect?.person?.cpf}-Atestado_termino_procuradoria`}
                title="Anexos comprobatórios de fim da atividade: "
                attachments={activityData.attachments}
                updateAttachments={handleUpdateAttachments}
                error={activityErrors.attachments}
              />

              <div className={classes.fabContainer}>
                <Tooltip title="Adicionar atividade">
                  <Fab
                    onClick={handleValidateAndUpdateState}
                    disabled={formState?.items?.length === 10}
                    size="small"
                    color="primary"
                  >
                    <Add />
                  </Fab>
                </Tooltip>
              </div>
            </div>
          ) : null}
        </div>
        {formState?.items?.length > 0 ? (
          <div className={classes.answersContainer}>
            {addKeyToObjectsArray(formState.items)?.map((activity, index) => (
              <AnswerCard
                key={activity.key}
                index={index}
                handleRemove={handleRemoveActivity}
                mustRemove={formState.hasProxyActivities === false}
              >
                {buildActivity(activity)}
              </AnswerCard>
            ))}
          </div>
        ) : null}
      </div>
      <div className={classes.buttonsContainer}>
        <QuestionButton
          variant="contained"
          onClick={validateAndSave}
          loading={loading}
          saved={saved}
          edited={edited}
          disabled={
            (formState.hasProxyActivities && formState.items.length === 0) ||
            (saved && !edited)
          }
        />
      </div>
    </div>
  )
}
